import { useCaptureEventsV2, useUserState } from "@gx1/gx-npm-lib";
import { GartnerFooter, Loader, SnackbarBanner } from "@gx1/gx-npm-ui";
import { Fade } from "@mui/material";
import classnames from "classnames";
import { useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { navigateToUrl } from "single-spa";
import EvaluationsComponent from "./sections/evaluations/evaluations.component";
import FTUXBanner from "./sections/ftux-banner/ftux-banner.component";
import Header from "./sections/header/header.component";
import ResearchHistoryComponent from "./sections/research-history/research-history.component";
import Tasks from "./sections/tasks/tasks.component";
import { ClientEvent } from "./app.constants";
import { HomeAppContext } from "./app.context";
import styles from "./app.styles.module.scss";

const App = () => {
  const [searchParams] = useSearchParams();
  const captureEvents = useCaptureEventsV2();
  const {
    initiatives,
    isLoading,
    saveDataErrorStatus,
    showSnackBar,
    snackbarMessage,
    setChecklistTasks,
    setError,
    setInitiatives,
    setLoading,
    setSaveDataErrorStatus,
    setRecentProductViews,
    setRecentTemplateViews,
    setShowSnackBar,
  } = useContext(HomeAppContext);
  const { hasInitiatives, hasTasks, hasUserState, isEntitled } = useUserState();

  useEffect(() => {
    if (hasUserState && hasTasks && !hasInitiatives && !isEntitled) {
      navigateToUrl("/s/tasks-dashboard");
    }
  }, [hasUserState, hasTasks, hasInitiatives, isEntitled]);

  useEffect(() => {
    const gxtmSource = searchParams.get("gxtm_source") || "";
    const gxtmType = searchParams.get("gxtm_type") || "";
    const gxtmPage = searchParams.get("gxtm_page") || "";
    captureEvents([{ eventType: ClientEvent.HOME_PAGE_VIEWED, metaData: { gxtmSource, gxtmType, gxtmPage } }]);
  }, [captureEvents, searchParams]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const response = await window.fetch(`${window.gxApiConnection}/api/v2/user/home`, { credentials: "include", method: "GET" });
        if (!response.ok) {
          throw new Error();
        }
        const data = await response.json();
        if (!data?.data) {
          throw new Error();
        }
        const { checklistTasks, initiatives: respInitiatives, recentProductViews, recentTemplateViews } = data.data;
        setChecklistTasks(Array.isArray(checklistTasks) ? checklistTasks : []);
        setInitiatives(Array.isArray(respInitiatives) ? respInitiatives : []);
        setRecentProductViews(Array.isArray(recentProductViews) ? recentProductViews : []);
        setRecentTemplateViews(Array.isArray(recentTemplateViews) ? recentTemplateViews : []);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [setError, setLoading, setChecklistTasks, setInitiatives, setRecentProductViews, setRecentTemplateViews]);

  return (
    <section className={styles.root}>
      <div className={styles.wrapper}>
        {isLoading && (
          <div className={styles.loader}>
            <p aria-hidden="true" className={styles.lcp}>{`_ `.repeat(200)}</p>
            <Loader size={100} />
          </div>
        )}
        <div className={classnames(styles.overlay, !isLoading && styles.open)}>
          {!isLoading && (
            <div className={styles.content}>
              <Header />
              <div className={styles.body}>
                <div className={styles.main}>
                  <div className={classnames(styles.ftux, !initiatives.length && !isLoading && styles.show)}>
                    <Fade in={!initiatives.length && !isLoading} timeout={1300}>
                      <div>
                        <FTUXBanner />
                      </div>
                    </Fade>
                  </div>
                  <EvaluationsComponent />
                  <Tasks />
                </div>
                <div className={styles.side}>
                  <ResearchHistoryComponent />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <GartnerFooter />
      <SnackbarBanner
        isDefaultErrorMessage={true}
        isOpen={saveDataErrorStatus}
        setIsOpen={setSaveDataErrorStatus}
      />
      <SnackbarBanner
        isOpen={showSnackBar}
        message={snackbarMessage}
        setIsOpen={setShowSnackBar}
        type="SUCCESS"
      />
    </section>
  );
};

export default App;
